/**
 * Telemetry module — the ONLY file that calls PostHog directly.
 * All event tracking goes through the Telemetry object.
 */
import posthog from 'posthog-js';
import type {
  SessionStartedProps,
  SessionEndedProps,
  PageViewedProps,
  ProjectSwitchedProps,
  UserSignedInProps,
  UserSignedOutProps,
  ReviewScanStartedProps,
  ReviewScanCompletedProps,
  ReviewScanFailedProps,
  ReviewScanCancelledProps,
  ReviewCacheClearedProps,
  ReviewResultsViewedProps,
  FindingDismissedProps,
  FindingUndismissedProps,
  ReviewTileFilterClickedProps,
  ExplorerViewedProps,
  DocumentOpenedInWordProps,
  DocumentDiffStartedProps,
  DocumentDiffViewedProps,
  ViewerOpenedProps,
  ViewerSectionRenderedProps,
  ViewerRenderFailedProps,
  ClearSectionSignedOffProps,
  ClearSectionSignOffRevokedProps,
  ClearScanStartedProps,
  ClearScanCompletedProps,
  ResolveAnalysisStartedProps,
  ResolveAnalysisCompletedProps,
  ClassificationTypeSetProps,
  ClassificationSuggestionDismissedProps,
  ClassificationConfirmedProps,
  ClassificationResetProps,
  ClassificationViewedProps,
  ClassificationUnexpectedSectionsBulkAcceptedProps,
  ReadinessDeclaredProps,
  ReviewSummaryViewedProps,
  SubmissionFocusChangedProps,
  ProjectCreatedProps,
  ProjectDeletedProps,
  ProjectFolderRulesConfiguredProps,
  LlmSettingsChangedProps,
  FreCompletedProps,
  ApiResponseSlowProps,
  ErrorSwallowedProps,
} from './telemetry-types';

declare const __APP_VERSION__: string;

// --- PostHog init ---

const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY || '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST || 'https://us.i.posthog.com';

let initialized = false;

export function initTelemetry(): void {
  if (initialized) return;
  if (!POSTHOG_API_KEY) {
    console.warn('[telemetry] PostHog API key not configured — telemetry disabled');
    return;
  }
  posthog.init(POSTHOG_API_KEY, {
    api_host: POSTHOG_HOST,
    autocapture: false,
    capture_pageview: false,
  });
  initialized = true;
  initSession();
}

// --- Session store (module-level) ---

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

let sessionId = '';
let sessionStartedAt = 0;
let currentScreen = '';

// Context synced from React providers via setTelemetryContext()
let contextUserId = 'anonymous';
let contextUserRole = 'unknown';
let contextProjectId = 'none';
let contextDevMode = false;
let contextOrgId = 'none';

function initSession(): void {
  sessionId = generateUUID();
  sessionStartedAt = Date.now();
  currentScreen = '';
}

export function setCurrentScreen(path: string): void {
  currentScreen = path;
}

export function setTelemetryContext(ctx: {
  userId?: string;
  userRole?: string;
  projectId?: string;
  orgId?: string;
  devMode?: boolean;
}): void {
  if (ctx.userId !== undefined) contextUserId = ctx.userId || 'anonymous';
  if (ctx.userRole !== undefined) contextUserRole = ctx.userRole || 'unknown';
  if (ctx.projectId !== undefined) contextProjectId = ctx.projectId || 'none';
  if (ctx.orgId !== undefined) contextOrgId = ctx.orgId || 'none';
  if (ctx.devMode !== undefined) contextDevMode = ctx.devMode;
}

export function getSessionDurationSeconds(): number {
  if (!sessionStartedAt) return 0;
  return Math.round((Date.now() - sessionStartedAt) / 1000);
}

// --- Common properties merged into every event ---

function getCommonProps(): Record<string, unknown> {
  return {
    user_id: contextUserId,
    user_role: contextUserRole,
    project_id: contextProjectId,
    session_id: sessionId,
    screen_name: currentScreen,
    app_version: typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : 'unknown',
    dev_mode: contextDevMode,
    org_id: contextOrgId,
    // Partition events by deploy environment in PostHog. Sourced from
    // Vercel's VERCEL_ENV at build time via vite.config.ts. See A2F-785.
    env: __APP_ENV__,
  };
}

function capture(eventName: string, props: object = {}): void {
  if (!initialized) return;
  posthog.capture(eventName, { ...getCommonProps(), ...(props as Record<string, unknown>) });
}

// --- Telemetry API — one function per event ---

export const Telemetry = {
  // Identification
  identify: (userId: string, props: Record<string, unknown> = {}) => {
    if (!initialized) return;
    posthog.identify(userId, props);
  },
  setOrgGroup: (orgId: string | null | undefined) => {
    if (!initialized) return;
    if (!orgId) return; // no-op for null/'' — never group on a missing org
    posthog.group('org', orgId);
  },
  reset: () => {
    if (!initialized) return;
    posthog.reset();
  },
  /**
   * A2F-366: register org_id + user_role as PostHog super properties so even
   * SDK-internal events ($identify, $set) that bypass capture()/getCommonProps
   * carry them. Belt-and-suspenders with getCommonProps (which already merges
   * both into every capture()'d event). Values are passed in (not read from the
   * context module vars) because TelemetrySync syncs those via a useEffect that
   * runs AFTER AuthContext's synchronous identify() — so the module vars are
   * stale at call time. Caller sources them from the same activeOrgId ?? orgId /
   * role used by identify(). Persists until reset() on logout.
   */
  registerSuperProperties: (props: { orgId: string | null; userRole: string }) => {
    if (!initialized) return;
    posthog.register({
      org_id: props.orgId || 'none',
      user_role: props.userRole || 'unknown',
    });
  },

  // Session
  sessionStarted: (props: SessionStartedProps = {}) => capture('session_started', props),
  sessionEnded: (props: SessionEndedProps) => capture('session_ended', props),
  pageViewed: (props: PageViewedProps) => capture('page_viewed', props),
  projectSwitched: (props: ProjectSwitchedProps) => capture('project_switched', props),

  // Auth
  userSignedIn: (props: UserSignedInProps) => capture('user_signed_in', props),
  userSignedOut: (props: UserSignedOutProps) => capture('user_signed_out', props),

  // Review — Scans
  reviewScanStarted: (props: ReviewScanStartedProps) => capture('review_scan_started', props),
  reviewScanCompleted: (props: ReviewScanCompletedProps) => capture('review_scan_completed', props),
  reviewScanFailed: (props: ReviewScanFailedProps) => capture('review_scan_failed', props),
  reviewScanCancelled: (props: ReviewScanCancelledProps) => capture('review_scan_cancelled', props),
  reviewCacheCleared: (props: ReviewCacheClearedProps) => capture('review_cache_cleared', props),
  reviewResultsViewed: (props: ReviewResultsViewedProps) => capture('review_results_viewed', props),

  // Review — Findings
  findingDismissed: (props: FindingDismissedProps) => capture('finding_dismissed', props),
  findingUndismissed: (props: FindingUndismissedProps) => capture('finding_undismissed', props),
  reviewTileFilterClicked: (props: ReviewTileFilterClickedProps) => capture('review_tile_filter_clicked', props),

  // See — Explorer
  explorerViewed: (props: ExplorerViewedProps) => capture('explorer_viewed', props),
  documentOpenedInWord: (props: DocumentOpenedInWordProps) => capture('document_opened_in_word', props),
  documentDiffStarted: (props: DocumentDiffStartedProps) => capture('document_diff_started', props),
  documentDiffViewed: (props: DocumentDiffViewedProps) => capture('document_diff_viewed', props),

  // See — Viewer
  viewerOpened: (props: ViewerOpenedProps) => capture('viewer_opened', props),
  viewerSectionRendered: (props: ViewerSectionRenderedProps) => capture('viewer_section_rendered', props),
  viewerRenderFailed: (props: ViewerRenderFailedProps) => capture('viewer_render_failed', props),

  // Clear
  clearSectionSignedOff: (props: ClearSectionSignedOffProps) => capture('clear_section_signed_off', props),
  clearSectionSignOffRevoked: (props: ClearSectionSignOffRevokedProps) => capture('clear_section_sign_off_revoked', props),
  clearScanStarted: (props: ClearScanStartedProps = {}) => capture('clear_scan_started', props),
  clearScanCompleted: (props: ClearScanCompletedProps) => capture('clear_scan_completed', props),

  // Resolve
  resolveAnalysisStarted: (props: ResolveAnalysisStartedProps) => capture('resolve_analysis_started', props),
  resolveAnalysisCompleted: (props: ResolveAnalysisCompletedProps) => capture('resolve_analysis_completed', props),
  classificationTypeSet: (props: ClassificationTypeSetProps) => capture('classification_type_set', props),
  classificationSuggestionDismissed: (props: ClassificationSuggestionDismissedProps) => capture('classification_suggestion_dismissed', props),
  classificationConfirmed: (props: ClassificationConfirmedProps) => capture('classification_confirmed', props),
  classificationReset: (props: ClassificationResetProps) => capture('classification_reset', props),
  classificationViewed: (props: ClassificationViewedProps) => capture('classification_viewed', props),
  classificationUnexpectedSectionsBulkAccepted: (props: ClassificationUnexpectedSectionsBulkAcceptedProps) =>
    capture('classification_unexpected_sections_bulk_accepted', props),

  // Review Summary / global focus
  readinessDeclared: (props: ReadinessDeclaredProps) => capture('readiness_declared', props),
  reviewSummaryViewed: (props: ReviewSummaryViewedProps = {}) => capture('review_summary_viewed', props),
  submissionFocusChanged: (props: SubmissionFocusChangedProps) => capture('submission_focus_changed', props),

  // Settings
  projectCreated: (props: ProjectCreatedProps) => capture('project_created', props),
  projectDeleted: (props: ProjectDeletedProps = {}) => capture('project_deleted', props),
  projectFolderRulesConfigured: (props: ProjectFolderRulesConfiguredProps) => capture('project_folder_rules_configured', props),
  llmSettingsChanged: (props: LlmSettingsChangedProps) => capture('llm_settings_changed', props),

  // FRE
  freCompleted: (props: FreCompletedProps) => capture('fre_completed', props),

  // Performance
  apiResponseSlow: (props: ApiResponseSlowProps) => capture('api_response_slow', props),

  // Error Tracking
  errorSwallowed: (props: ErrorSwallowedProps) => capture('error_swallowed', props),
};
